<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import KanbanAPI from 'dashboard/api/kanban';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const PANEL_TABS = Object.freeze({
  MOVE: 'move',
  AGENTS: 'agents',
  DETAILS: 'details',
});

const DETAIL_TABS = Object.freeze({
  NOTES: 'notes',
  CHECKLIST: 'checklist',
  ATTACHMENTS: 'attachments',
});

const PRIORITY_LABELS = Object.freeze({
  low: 'Baixa',
  medium: 'Media',
  high: 'Alta',
  urgent: 'Urgente',
});

const PRIORITY_STYLES = Object.freeze({
  low: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200',
  medium: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200',
  high: 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200',
  urgent: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200',
});

const store = useStore();
const route = useRoute();
const router = useRouter();

const activePanelTab = ref(PANEL_TABS.DETAILS);
const activeDetailTab = ref(DETAIL_TABS.NOTES);
const selectedStageId = ref(null);
const selectedMoveFunnelId = ref(null);
const selectedMoveFunnelStages = ref([]);
const fetchedFunnelStages = ref([]);
const kanbanHistory = ref([]);
const isMoving = ref(false);
const isLoadingMoveStages = ref(false);
const isLoadingKanbanDetails = ref(false);
const newNote = ref('');
const isSavingNote = ref(false);
const expandedNote = ref(null);
const newChecklistItem = ref('');
const isSavingChecklist = ref(false);
const availableFunnels = ref([]);
const selectedFunnelId = ref(null);
const selectedStageIdForLink = ref(null);
const selectedFunnelStages = ref([]);
const isLoadingFunnels = ref(false);
const isLoadingLinkStages = ref(false);
const isLinking = ref(false);
const attachmentInputRef = ref(null);
const isUploadingAttachments = ref(false);
const isSavingPinnedAttachments = ref(false);

const conversation = computed(
  () => store.getters.getConversationById(props.conversationId) || {}
);

const conversationId = computed(() => Number(conversation.value?.id || props.conversationId));
const kanbanStage = computed(() => conversation.value.kanban_stage || null);
const kanbanFunnel = computed(() => kanbanStage.value?.kanban_funnel || null);
const assignee = computed(() => conversation.value?.meta?.assignee || null);
const currentUser = computed(() => store.getters.getCurrentUser || {});
const hasKanbanData = computed(
  () => Boolean(kanbanStage.value && kanbanFunnel.value)
);

const priorityValue = computed(() => conversation.value.priority || 'medium');
const priorityLabel = computed(
  () => PRIORITY_LABELS[priorityValue.value] || 'Media'
);
const priorityStyle = computed(
  () => PRIORITY_STYLES[priorityValue.value] || PRIORITY_STYLES.medium
);

const selectedLinkFunnelId = computed(() => {
  const parsedId = Number(selectedFunnelId.value);
  return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
});

const selectedLinkStageId = computed(() => {
  const parsedId = Number(selectedStageIdForLink.value);
  return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
});

const selectedMoveFunnelIdValue = computed(() => {
  const parsedId = Number(selectedMoveFunnelId.value);
  return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
});

const hasAvailableFunnels = computed(() => availableFunnels.value.length > 0);
const canLinkToPipeline = computed(
  () => Boolean(selectedLinkStageId.value) && !isLinking.value
);
const conversationIdentifierCandidates = computed(() => {
  const rawCandidates = [
    conversation.value?.display_id,
    props.conversationId,
    conversation.value?.additional_attributes?.kanban_item_id,
    conversation.value?.id,
  ];

  const uniqueCandidates = [];
  rawCandidates.forEach(candidate => {
    if (candidate === null || candidate === undefined) return;
    const normalized = String(candidate).trim();
    if (!normalized) return;
    if (uniqueCandidates.includes(normalized)) return;
    uniqueCandidates.push(normalized);
  });

  return uniqueCandidates;
});

const availableMoveFunnels = computed(() => {
  const uniqueFunnels = new Map();

  (availableFunnels.value || []).forEach(funnel => {
    const normalizedFunnelId = Number(funnel?.id);
    if (Number.isInteger(normalizedFunnelId) && normalizedFunnelId > 0) {
      uniqueFunnels.set(normalizedFunnelId, funnel);
    }
  });

  const currentFunnelId = Number(kanbanFunnel.value?.id);
  if (
    Number.isInteger(currentFunnelId) &&
    currentFunnelId > 0 &&
    !uniqueFunnels.has(currentFunnelId)
  ) {
    uniqueFunnels.set(currentFunnelId, kanbanFunnel.value);
  }

  return Array.from(uniqueFunnels.values());
});

const linkFunnelStages = computed(() => {
  return [...selectedFunnelStages.value].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  );
});

const kanbanNotes = computed(() => {
  const sourceNotes = conversation.value.custom_attributes?.kanban_notes;
  if (!Array.isArray(sourceNotes)) return [];
  return [...sourceNotes].sort(
    (a, b) => Number(b?.created_at || 0) - Number(a?.created_at || 0)
  );
});

const notesCount = computed(() => kanbanNotes.value.length);
const attachments = computed(() => store.getters.getSelectedChatAttachments || []);
const attachmentsCount = computed(() => attachments.value.length);
const pinnedAttachmentIds = computed(() => {
  const sourceIds = conversation.value.custom_attributes?.kanban_pinned_attachment_ids;
  if (!Array.isArray(sourceIds)) return [];
  return sourceIds
    .map(id => Number(id))
    .filter(id => Number.isInteger(id) && id > 0);
});

const pinnedAttachments = computed(() => {
  const pinnedIdsSet = new Set(pinnedAttachmentIds.value);
  return attachments.value.filter(attachment =>
    pinnedIdsSet.has(Number(attachment?.id))
  );
});

const regularAttachments = computed(() => {
  const pinnedIdsSet = new Set(pinnedAttachmentIds.value);
  return attachments.value.filter(
    attachment => !pinnedIdsSet.has(Number(attachment?.id))
  );
});

const checklistItems = computed(() => {
  const sourceItems = conversation.value.custom_attributes?.kanban_checklist_items;
  if (!Array.isArray(sourceItems)) return [];

  return sourceItems.map((item, index) => ({
    id: item?.id || `${conversationId.value}-${index}`,
    text: item?.text || item?.title || '',
    done: item?.done === true,
  }));
});

const completedChecklistCount = computed(
  () => checklistItems.value.filter(item => item.done).length
);
const checklistProgress = computed(
  () => `${completedChecklistCount.value}/${checklistItems.value.length}`
);
const stageChangesCount = computed(() => kanbanHistory.value.length);

const resolveTimestampMs = value => {
  const rawTimestamp = Number(value);
  if (Number.isFinite(rawTimestamp)) {
    return rawTimestamp > 1e12 ? rawTimestamp : rawTimestamp * 1000;
  }
  return value;
};

const timeInStage = computed(() => {
  if (!conversation.value.kanban_stage_changed_at) return null;

  const changedAt = new Date(
    resolveTimestampMs(conversation.value.kanban_stage_changed_at)
  );
  if (Number.isNaN(changedAt.getTime())) return null;

  const diffMs = Date.now() - changedAt.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `ha ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  if (diffHours > 0) return `ha ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  return 'ha poucos minutos';
});

const formatTimestamp = value => {
  if (!value) return 'Sem data';
  const parsedDate = new Date(resolveTimestampMs(value));
  if (Number.isNaN(parsedDate.getTime())) return 'Sem data';
  return parsedDate.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const funnelStagesFromConversation = computed(() => {
  const stages = kanbanFunnel.value?.kanban_stages;
  return Array.isArray(stages) ? stages : [];
});

const funnelStages = computed(() => {
  const sourceStages = fetchedFunnelStages.value.length
    ? fetchedFunnelStages.value
    : funnelStagesFromConversation.value;

  return [...sourceStages].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const stageOptions = computed(() => {
  const currentFunnelId = Number(kanbanFunnel.value?.id);
  if (
    selectedMoveFunnelIdValue.value &&
    selectedMoveFunnelIdValue.value !== currentFunnelId
  ) {
    return [...selectedMoveFunnelStages.value].sort(
      (a, b) => (a.position || 0) - (b.position || 0)
    );
  }

  if (funnelStages.value.length) return funnelStages.value;
  return kanbanStage.value ? [kanbanStage.value] : [];
});

const canMoveToSelectedStage = computed(() => {
  const targetStageId = Number(selectedStageId.value);
  const currentStageId = Number(kanbanStage.value?.id);
  return (
    Number.isInteger(targetStageId) &&
    Number.isInteger(currentStageId) &&
    targetStageId > 0 &&
    targetStageId !== currentStageId &&
    !isMoving.value
  );
});

const isCurrentStage = stage =>
  Number(stage?.id) === Number(kanbanStage.value?.id);

const isPastStage = stage => {
  const stagePosition = Number(stage?.position || 0);
  const currentPosition = Number(kanbanStage.value?.position || 0);
  return stagePosition > 0 && currentPosition > 0 && stagePosition < currentPosition;
};

const ensureMoveFunnelSelection = () => {
  if (!hasKanbanData.value) return;

  const moveFunnels = availableMoveFunnels.value;
  const availableFunnelIds = moveFunnels
    .map(funnel => Number(funnel?.id))
    .filter(id => Number.isInteger(id) && id > 0);

  if (!availableFunnelIds.length) {
    selectedMoveFunnelId.value = null;
    return;
  }

  const currentSelectedFunnelId = Number(selectedMoveFunnelId.value);
  if (availableFunnelIds.includes(currentSelectedFunnelId)) return;

  const currentConversationFunnelId = Number(kanbanFunnel.value?.id);
  if (availableFunnelIds.includes(currentConversationFunnelId)) {
    selectedMoveFunnelId.value = currentConversationFunnelId;
    return;
  }

  selectedMoveFunnelId.value = availableFunnelIds[0];
};

const fetchAvailableFunnels = async () => {
  if (isLoadingFunnels.value) return;

  isLoadingFunnels.value = true;
  try {
    const { data } = await KanbanAPI.getFunnels({ active: true });
    availableFunnels.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error loading available funnels:', error);
    availableFunnels.value = [];
    useAlert('Falha ao carregar funis');
  } finally {
    isLoadingFunnels.value = false;
  }
};

const fetchSelectedFunnelStages = async funnelId => {
  if (!funnelId) {
    selectedFunnelStages.value = [];
    return;
  }

  isLoadingLinkStages.value = true;
  try {
    const { data } = await KanbanAPI.getFunnel(funnelId);
    selectedFunnelStages.value = Array.isArray(data?.kanban_stages)
      ? data.kanban_stages
      : [];
  } catch (error) {
    console.error('Error loading selected funnel stages:', error);
    selectedFunnelStages.value = [];
    useAlert('Falha ao carregar etapas');
  } finally {
    isLoadingLinkStages.value = false;
  }
};

const loadMoveFunnelStages = async funnelId => {
  const normalizedFunnelId = Number(funnelId);
  if (!Number.isInteger(normalizedFunnelId) || normalizedFunnelId <= 0) {
    selectedMoveFunnelStages.value = [];
    return;
  }

  if (normalizedFunnelId === Number(kanbanFunnel.value?.id)) {
    selectedMoveFunnelStages.value = [];
    selectedStageId.value = Number(kanbanStage.value?.id) || null;
    return;
  }

  isLoadingMoveStages.value = true;
  try {
    const { data } = await KanbanAPI.getFunnel(normalizedFunnelId);
    selectedMoveFunnelStages.value = Array.isArray(data?.kanban_stages)
      ? data.kanban_stages
      : [];
  } catch (error) {
    console.error('Error loading move funnel stages:', error);
    selectedMoveFunnelStages.value = [];
    useAlert('Falha ao carregar etapas do funil');
  } finally {
    isLoadingMoveStages.value = false;
  }
};

const loadFunnelStages = async funnelId => {
  if (!funnelId) {
    fetchedFunnelStages.value = [];
    return;
  }

  if (funnelStagesFromConversation.value.length) {
    fetchedFunnelStages.value = [];
    return;
  }

  try {
    const { data } = await KanbanAPI.getFunnel(funnelId);
    fetchedFunnelStages.value = data?.kanban_stages || data?.stages || [];
  } catch (error) {
    fetchedFunnelStages.value = [];
    console.error('Error loading funnel stages:', error);
  }
};

const loadKanbanDetails = async () => {
  if (!conversationId.value) return;

  isLoadingKanbanDetails.value = true;
  try {
    const { data } = await KanbanAPI.getConversationKanban(conversationId.value);
    kanbanHistory.value = Array.isArray(data?.history) ? data.history : [];

    const stagesFromKanbanDetails = data?.funnel?.kanban_stages;
    if (Array.isArray(stagesFromKanbanDetails) && stagesFromKanbanDetails.length) {
      fetchedFunnelStages.value = stagesFromKanbanDetails;
    }
  } catch (error) {
    kanbanHistory.value = [];
    console.error('Error loading kanban details:', error);
  } finally {
    isLoadingKanbanDetails.value = false;
  }
};

const syncConversationContext = async conversationKey => {
  if (!conversationKey) return;

  const numericConversationKey = Number(conversationKey);
  const normalizedConversationKey = Number.isInteger(numericConversationKey)
    ? numericConversationKey
    : conversationKey;

  await Promise.allSettled([
    store.dispatch('getConversation', normalizedConversationKey),
    store.dispatch('fetchAllAttachments', normalizedConversationKey),
    loadKanbanDetails(),
  ]);
};

watch(
  () => props.conversationId,
  newConversationId => {
    newNote.value = '';
    newChecklistItem.value = '';
    selectedFunnelId.value = null;
    selectedStageIdForLink.value = null;
    selectedFunnelStages.value = [];
    selectedMoveFunnelId.value = null;
    selectedMoveFunnelStages.value = [];
    expandedNote.value = null;
    syncConversationContext(newConversationId);
  },
  { immediate: true }
);

watch(
  () => hasKanbanData.value,
  hasStage => {
    if (!availableFunnels.value.length) {
      fetchAvailableFunnels();
    }
    if (!hasStage) {
      selectedMoveFunnelId.value = null;
      selectedMoveFunnelStages.value = [];
      return;
    }
    ensureMoveFunnelSelection();
  },
  { immediate: true }
);

watch(
  () => availableMoveFunnels.value,
  () => {
    ensureMoveFunnelSelection();
  },
  { deep: true }
);

watch(
  () => selectedLinkFunnelId.value,
  funnelId => {
    selectedStageIdForLink.value = null;
    selectedFunnelStages.value = [];
    if (funnelId) {
      fetchSelectedFunnelStages(funnelId);
    }
  }
);

watch(
  () => kanbanFunnel.value?.id,
  funnelId => {
    selectedMoveFunnelId.value = Number(funnelId) || null;
    selectedMoveFunnelStages.value = [];
    loadFunnelStages(funnelId);
  },
  { immediate: true }
);

watch(
  () => activePanelTab.value,
  activeTab => {
    if (activeTab === PANEL_TABS.MOVE) {
      ensureMoveFunnelSelection();
    }
  }
);

watch(
  () => kanbanStage.value?.id,
  stageId => {
    selectedStageId.value = Number(stageId) || null;
  },
  { immediate: true }
);

watch(
  () => selectedMoveFunnelIdValue.value,
  funnelId => {
    if (!hasKanbanData.value) return;

    selectedStageId.value = null;
    loadMoveFunnelStages(funnelId);
  }
);

const moveConversationToStage = async (targetStageId, reason = 'Sidebar timeline move') => {
  if (!hasKanbanData.value) return;

  const normalizedTargetStageId = Number(targetStageId);
  const currentStageId = Number(kanbanStage.value?.id);
  if (
    !Number.isInteger(normalizedTargetStageId) ||
    normalizedTargetStageId <= 0 ||
    normalizedTargetStageId === currentStageId
  ) {
    return;
  }

  isMoving.value = true;
  try {
    await store.dispatch('kanban/moveConversation', {
      conversationId: conversationId.value,
      conversationDisplayId: conversation.value?.display_id || props.conversationId,
      fromStageId: Number.isInteger(currentStageId) ? currentStageId : null,
      toStageId: normalizedTargetStageId,
      reason,
    });
    await syncConversationContext(conversationId.value);
    useAlert('Etapa atualizada com sucesso');
  } catch (error) {
    console.error('Error moving conversation from widget:', error);
    useAlert('Falha ao atualizar etapa');
  } finally {
    isMoving.value = false;
  }
};

const onTimelineStageClick = stage => {
  moveConversationToStage(stage?.id, 'Sidebar timeline click');
};

const onMoveToSelectedStage = () => {
  moveConversationToStage(selectedStageId.value, 'Sidebar move tab');
};

const linkToPipeline = async () => {
  if (!canLinkToPipeline.value || !conversationIdentifierCandidates.value.length) return;

  isLinking.value = true;
  try {
    let hasLinked = false;
    let lastError = null;

    for (const identifier of conversationIdentifierCandidates.value) {
      try {
        await KanbanAPI.moveConversation(
          identifier,
          selectedLinkStageId.value,
          'Sidebar initial pipeline link'
        );
        hasLinked = true;
        break;
      } catch (error) {
        lastError = error;
        const statusCode = Number(error?.response?.status);
        // Try the next identifier only for lookup errors.
        if (statusCode !== 404 && statusCode !== 422) {
          break;
        }
      }
    }

    if (!hasLinked) {
      throw lastError || new Error('Unable to link conversation to pipeline');
    }

    await syncConversationContext(conversationId.value);

    selectedFunnelId.value = null;
    selectedStageIdForLink.value = null;
    selectedFunnelStages.value = [];
    useAlert('Conversa vinculada ao pipeline com sucesso');
  } catch (error) {
    console.error('Error linking conversation to pipeline:', error);
    const apiError = error?.response?.data?.error || error?.response?.data?.message;
    const statusCode = Number(error?.response?.status);
    if (statusCode === 403) {
      useAlert('Sem permissao para vincular esta conversa ao pipeline');
      return;
    }
    useAlert(apiError ? `Erro ao vincular conversa: ${apiError}` : 'Erro ao vincular conversa ao pipeline');
  } finally {
    isLinking.value = false;
  }
};

const isLongNote = note => {
  const noteContent = String(note?.content || '');
  const hasManyLines = noteContent.split('\n').length > 4;
  return hasManyLines || noteContent.length > 220;
};

const getNotePreview = note => {
  const noteContent = String(note?.content || '');
  if (!isLongNote(note)) return noteContent;

  const croppedByLines = noteContent.split('\n').slice(0, 4).join('\n');
  const croppedContent = croppedByLines.length > 220
    ? croppedByLines.slice(0, 220)
    : croppedByLines;
  return `${croppedContent.trimEnd()}...`;
};

const openExpandedNote = note => {
  expandedNote.value = note || null;
};

const closeExpandedNote = () => {
  expandedNote.value = null;
};

const addKanbanNote = async () => {
  const noteContent = newNote.value.trim();
  if (!noteContent || !conversationId.value || isSavingNote.value) return;

  isSavingNote.value = true;
  try {
    await KanbanAPI.createConversationNote(
      conversationId.value,
      noteContent,
      currentUser.value?.name || 'Agente'
    );
    await syncConversationContext(conversationId.value);
    newNote.value = '';
    useAlert('Nota adicionada com sucesso');
  } catch (error) {
    console.error('Error adding kanban note:', error);
    useAlert('Falha ao adicionar nota');
  } finally {
    isSavingNote.value = false;
  }
};

const persistChecklistItems = async nextItems => {
  if (!conversationId.value) return;

  isSavingChecklist.value = true;
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: conversationId.value,
      customAttributes: {
        ...(conversation.value.custom_attributes || {}),
        kanban_checklist_items: nextItems,
      },
    });
  } catch (error) {
    console.error('Error updating checklist:', error);
    useAlert('Falha ao atualizar checklist');
  } finally {
    isSavingChecklist.value = false;
  }
};

const addChecklistItem = async () => {
  const itemText = newChecklistItem.value.trim();
  if (!itemText) return;

  const nextItems = [
    ...checklistItems.value,
    { id: Date.now(), text: itemText, done: false },
  ];
  await persistChecklistItems(nextItems);
  newChecklistItem.value = '';
};

const toggleChecklistItem = async itemId => {
  const nextItems = checklistItems.value.map(item =>
    item.id === itemId ? { ...item, done: !item.done } : item
  );
  await persistChecklistItems(nextItems);
};

const removeChecklistItem = async itemId => {
  const nextItems = checklistItems.value.filter(item => item.id !== itemId);
  await persistChecklistItems(nextItems);
};

const openAttachmentPicker = () => {
  if (isUploadingAttachments.value) return;
  attachmentInputRef.value?.click();
};

const handleAttachmentInput = async event => {
  const selectedFiles = Array.from(event?.target?.files || []);
  if (!selectedFiles.length || !conversationId.value) return;

  isUploadingAttachments.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: conversationId.value,
      message: '',
      private: true,
      sender: 'agent',
      files: selectedFiles,
    });
    await store.dispatch('fetchAllAttachments', conversationId.value);
    useAlert('Anexos enviados com sucesso');
  } catch (error) {
    console.error('Error uploading attachments from widget:', error);
    useAlert('Falha ao enviar anexos');
  } finally {
    isUploadingAttachments.value = false;
    if (event?.target) {
      event.target.value = '';
    }
  }
};

const isAttachmentPinned = attachmentId => {
  return pinnedAttachmentIds.value.includes(Number(attachmentId));
};

const persistPinnedAttachmentIds = async nextPinnedIds => {
  if (!conversationId.value || isSavingPinnedAttachments.value) return;

  isSavingPinnedAttachments.value = true;
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: conversationId.value,
      customAttributes: {
        ...(conversation.value.custom_attributes || {}),
        kanban_pinned_attachment_ids: nextPinnedIds,
      },
    });
  } catch (error) {
    console.error('Error saving pinned attachments:', error);
    useAlert('Falha ao salvar anexos guardados');
  } finally {
    isSavingPinnedAttachments.value = false;
  }
};

const togglePinnedAttachment = async attachmentId => {
  const normalizedAttachmentId = Number(attachmentId);
  if (!Number.isInteger(normalizedAttachmentId) || normalizedAttachmentId <= 0) {
    return;
  }

  const pinnedIdsSet = new Set(pinnedAttachmentIds.value);
  if (pinnedIdsSet.has(normalizedAttachmentId)) {
    pinnedIdsSet.delete(normalizedAttachmentId);
  } else {
    pinnedIdsSet.add(normalizedAttachmentId);
  }

  await persistPinnedAttachmentIds([...pinnedIdsSet]);
};

const goToKanban = () => {
  if (kanbanFunnel.value?.id) {
    router.push({
      name: 'kanban_board',
      params: {
        accountId: route.params.accountId,
        funnelId: kanbanFunnel.value.id,
      },
    });
    return;
  }

  router.push({
    name: 'kanban_settings',
    params: { accountId: route.params.accountId },
  });
};

</script>

<template>
  <div class="kanban-widget space-y-3">
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">Pipeline de Vendas</h3>
    </div>

    <div class="grid grid-cols-3 rounded-lg border border-slate-200 dark:border-slate-700 p-1 bg-slate-50 dark:bg-slate-800/70">
      <button
        class="kanban-tab"
        :class="{ 'is-active': activePanelTab === PANEL_TABS.MOVE }"
        @click="activePanelTab = PANEL_TABS.MOVE"
      >
        Mover
      </button>
      <button
        class="kanban-tab"
        :class="{ 'is-active': activePanelTab === PANEL_TABS.AGENTS }"
        @click="activePanelTab = PANEL_TABS.AGENTS"
      >
        Agentes
      </button>
      <button
        class="kanban-tab"
        :class="{ 'is-active': activePanelTab === PANEL_TABS.DETAILS }"
        @click="activePanelTab = PANEL_TABS.DETAILS"
      >
        Detalhes
      </button>
    </div>

    <div v-if="hasKanbanData" class="space-y-3">
      <div v-if="activePanelTab === PANEL_TABS.DETAILS" class="space-y-3">
        <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
          <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Timeline de Etapas</p>
          <div class="kanban-timeline">
            <button
              v-for="stage in funnelStages"
              :key="stage.id"
              class="timeline-stage"
              :class="{
                'is-current': isCurrentStage(stage),
                'is-past': isPastStage(stage),
              }"
              :disabled="isMoving"
              @click="onTimelineStageClick(stage)"
            >
              <span
                class="timeline-stage__dot"
                :style="{ backgroundColor: stage.color || '#3B82F6' }"
              />
              <span class="timeline-stage__label">
                {{ stage.name }}
              </span>
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <div class="kanban-stat-card">
            <p class="kanban-stat-card__label">Tempo na etapa</p>
            <p class="kanban-stat-card__value">{{ timeInStage || 'Sem registro' }}</p>
          </div>
          <div class="kanban-stat-card">
            <p class="kanban-stat-card__label">Notas</p>
            <p class="kanban-stat-card__value">{{ notesCount }}</p>
          </div>
          <div class="kanban-stat-card">
            <p class="kanban-stat-card__label">Checklist</p>
            <p class="kanban-stat-card__value">{{ checklistProgress }}</p>
          </div>
          <div class="kanban-stat-card">
            <p class="kanban-stat-card__label">Anexos</p>
            <p class="kanban-stat-card__value">{{ attachmentsCount }}</p>
          </div>
        </div>

        <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-2">
          <div class="flex items-center justify-between">
            <p class="text-xs font-semibold text-slate-500 dark:text-slate-400">
              Mudancas de etapa
            </p>
            <span class="text-xs font-semibold text-slate-700 dark:text-slate-200">
              {{ stageChangesCount }}
            </span>
          </div>
          <div v-if="isLoadingKanbanDetails" class="text-xs text-slate-500 dark:text-slate-400">
            Carregando historico...
          </div>
          <div
            v-else-if="kanbanHistory.length"
            class="space-y-1.5 max-h-28 overflow-y-auto"
          >
            <div
              v-for="historyItem in kanbanHistory.slice(0, 6)"
              :key="historyItem.id || `${historyItem.to_stage}-${historyItem.performed_at}`"
              class="text-xs text-slate-600 dark:text-slate-300 leading-4"
            >
              <strong class="text-slate-800 dark:text-slate-100">{{ historyItem.to_stage }}</strong>
              <span class="mx-1">por</span>
              <span>{{ historyItem.performed_by || 'Sistema' }}</span>
              <span class="mx-1">em</span>
              <span>{{ formatTimestamp(historyItem.performed_at) }}</span>
            </div>
          </div>
          <p v-else class="text-xs text-slate-500 dark:text-slate-400">Sem historico recente</p>
        </div>

        <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-3">
          <div class="grid grid-cols-3 rounded-lg border border-slate-200 dark:border-slate-700 p-1 bg-slate-50 dark:bg-slate-800/70">
            <button
              class="kanban-detail-tab"
              :class="{ 'is-active': activeDetailTab === DETAIL_TABS.NOTES }"
              @click="activeDetailTab = DETAIL_TABS.NOTES"
            >
              Notas
            </button>
            <button
              class="kanban-detail-tab"
              :class="{ 'is-active': activeDetailTab === DETAIL_TABS.CHECKLIST }"
              @click="activeDetailTab = DETAIL_TABS.CHECKLIST"
            >
              Checklist
            </button>
            <button
              class="kanban-detail-tab"
              :class="{ 'is-active': activeDetailTab === DETAIL_TABS.ATTACHMENTS }"
              @click="activeDetailTab = DETAIL_TABS.ATTACHMENTS"
            >
              Anexos
            </button>
          </div>

          <div v-if="activeDetailTab === DETAIL_TABS.NOTES" class="space-y-2.5">
            <div class="space-y-1.5 max-h-40 overflow-y-auto">
              <p
                v-if="!kanbanNotes.length"
                class="text-xs text-slate-500 dark:text-slate-400"
              >
                Nenhuma nota registrada no pipeline.
              </p>
              <div
                v-for="note in kanbanNotes.slice(0, 8)"
                :key="note.id"
                class="rounded-md border border-slate-100 bg-slate-50 dark:bg-slate-800/70 px-2 py-1.5"
              >
                <p class="text-xs text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                  {{ isLongNote(note) ? getNotePreview(note) : note.content }}
                </p>
                <p class="text-xxs text-slate-500 dark:text-slate-400 mt-1 flex items-center justify-between">
                  <span>{{ formatTimestamp(note.created_at) }}</span>
                  <span class="truncate max-w-[50%]">{{ note.created_by_name || 'Agente' }}</span>
                </p>
                <button
                  v-if="isLongNote(note)"
                  class="mt-1 inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xxs font-semibold text-blue-700 hover:bg-blue-100 hover:text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/60 dark:hover:text-blue-100"
                  @click="openExpandedNote(note)"
                >
                  Expandir nota
                </button>
              </div>
            </div>
            <div class="space-y-1.5">
              <textarea
                v-model="newNote"
                rows="2"
                class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900"
                placeholder="Adicionar nota do pipeline..."
              />
              <button
                class="w-full rounded-lg px-2.5 py-2 text-xs font-semibold text-white bg-blue-700 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500 transition-colors disabled:bg-slate-400 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
                :disabled="isSavingNote || !newNote.trim()"
                @click="addKanbanNote"
              >
                {{ isSavingNote ? 'Salvando...' : 'Salvar nota' }}
              </button>
            </div>
          </div>

          <div v-else-if="activeDetailTab === DETAIL_TABS.CHECKLIST" class="space-y-2.5">
            <div class="space-y-1.5 max-h-40 overflow-y-auto">
              <p
                v-if="!checklistItems.length"
                class="text-xs text-slate-500 dark:text-slate-400"
              >
                Checklist vazio.
              </p>
              <label
                v-for="item in checklistItems"
                :key="item.id"
                class="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50 dark:bg-slate-800/70 px-2 py-1.5"
              >
                <input
                  type="checkbox"
                  :checked="item.done"
                  class="!m-0"
                  @change="toggleChecklistItem(item.id)"
                />
                <span
                  class="text-xs text-slate-700 dark:text-slate-200 flex-1"
                  :class="{ 'line-through text-slate-400 dark:text-slate-500': item.done }"
                >
                  {{ item.text }}
                </span>
                <button
                  class="text-xxs text-rose-500 hover:text-rose-700"
                  @click.prevent="removeChecklistItem(item.id)"
                >
                  remover
                </button>
              </label>
            </div>
            <div class="flex gap-1.5">
              <input
                v-model="newChecklistItem"
                type="text"
                class="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900"
                placeholder="Nova tarefa..."
                @keyup.enter="addChecklistItem"
              />
              <button
                class="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white bg-slate-700 hover:bg-slate-800 transition-colors disabled:opacity-60"
                :disabled="isSavingChecklist"
                @click="addChecklistItem"
              >
                +
              </button>
            </div>
          </div>

          <div v-else class="space-y-2">
            <div class="flex items-center justify-between">
              <p class="text-xs font-semibold text-slate-600 dark:text-slate-300">
                Anexos da conversa
              </p>
              <button
                class="rounded-md px-2.5 py-1.5 text-xxs font-semibold text-white bg-blue-700 hover:bg-blue-800 transition-colors disabled:bg-slate-400 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
                :disabled="isUploadingAttachments"
                @click="openAttachmentPicker"
              >
                {{ isUploadingAttachments ? 'Enviando...' : 'Adicionar anexo' }}
              </button>
              <input
                ref="attachmentInputRef"
                type="file"
                multiple
                class="hidden"
                @change="handleAttachmentInput"
              />
            </div>

            <div v-if="pinnedAttachments.length" class="space-y-1">
              <p class="text-xxs font-semibold text-slate-500 dark:text-slate-400">Guardados no widget</p>
              <div
                v-for="attachment in pinnedAttachments"
                :key="`pinned-${attachment.id}`"
                class="rounded-md border border-emerald-200 bg-emerald-50 dark:border-emerald-700/60 dark:bg-emerald-950/30 px-2 py-1.5"
              >
                <div class="flex items-start justify-between gap-2">
                  <a
                    :href="attachment.data_url || attachment.dataUrl || '#'"
                    class="block flex-1 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <p class="text-xs font-medium text-slate-700 dark:text-slate-200">
                      {{ attachment.extension || attachment.file_type || attachment.fileType || 'arquivo' }}
                    </p>
                    <p class="text-xxs text-slate-500 dark:text-slate-400">
                      {{ formatTimestamp(attachment.created_at || attachment.createdAt) }}
                    </p>
                  </a>
                  <button
                    class="text-xxs font-semibold text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
                    :disabled="isSavingPinnedAttachments"
                    @click="togglePinnedAttachment(attachment.id)"
                  >
                    Remover
                  </button>
                </div>
              </div>
            </div>

            <p
              v-if="!attachments.length"
              class="text-xs text-slate-500 dark:text-slate-400"
            >
              Nenhum anexo encontrado.
            </p>

            <div
              v-for="attachment in regularAttachments"
              :key="attachment.id"
              class="rounded-md border border-slate-100 bg-slate-50 dark:bg-slate-800/70 px-2 py-1.5"
            >
              <div class="flex items-start justify-between gap-2">
                <a
                  :href="attachment.data_url || attachment.dataUrl || '#'"
                  class="block flex-1 hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  <p class="text-xs font-medium text-slate-700 dark:text-slate-200">
                    {{ attachment.extension || attachment.file_type || attachment.fileType || 'arquivo' }}
                  </p>
                  <p class="text-xxs text-slate-500 dark:text-slate-400">
                    {{ formatTimestamp(attachment.created_at || attachment.createdAt) }}
                  </p>
                </a>
                <button
                  class="text-xxs font-semibold text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
                  :disabled="isSavingPinnedAttachments"
                  @click="togglePinnedAttachment(attachment.id)"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="activePanelTab === PANEL_TABS.MOVE" class="space-y-2.5">
        <label class="text-xs font-medium text-slate-600 dark:text-slate-300" for="kanban-funnel-select">
          Escolher funil
        </label>
        <select
          id="kanban-funnel-select"
          v-model.number="selectedMoveFunnelId"
          class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900"
          :disabled="isMoving || isLoadingFunnels"
        >
          <option
            v-for="funnel in availableMoveFunnels"
            :key="funnel.id"
            :value="funnel.id"
          >
            {{ funnel.name }}
          </option>
        </select>

        <label class="text-xs font-medium text-slate-600 dark:text-slate-300" for="kanban-stage-select">
          Escolher nova etapa
        </label>
        <select
          id="kanban-stage-select"
          v-model.number="selectedStageId"
          class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900"
          :disabled="isLoadingMoveStages || isMoving || !stageOptions.length"
        >
          <option v-if="isLoadingMoveStages" :value="null">
            Carregando etapas...
          </option>
          <option v-else-if="!stageOptions.length" :value="null">
            Selecione um funil para carregar etapas
          </option>
          <option
            v-for="stage in stageOptions"
            :key="stage.id"
            :value="stage.id"
          >
            {{ stage.name }}
          </option>
        </select>
        <button
          class="w-full rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors"
          :class="canMoveToSelectedStage
            ? 'bg-blue-700 hover:bg-blue-800 shadow-sm ring-1 ring-blue-700/40'
            : 'bg-slate-300 text-slate-700 dark:bg-slate-700 dark:text-slate-200 cursor-not-allowed opacity-90'"
          :disabled="!canMoveToSelectedStage"
          @click="onMoveToSelectedStage"
        >
          {{ isMoving ? 'Atualizando...' : 'Aplicar etapa' }}
        </button>
      </div>

      <div v-else class="space-y-2.5">
        <div class="kanban-row">
          <span class="kanban-row__label text-slate-700 dark:text-slate-100 font-semibold">Responsavel atual</span>
          <span class="kanban-row__value text-slate-900 dark:text-white">
            {{ assignee?.name || 'Sem agente atribuido' }}
          </span>
        </div>
        <div class="kanban-row">
          <span class="kanban-row__label text-slate-700 dark:text-slate-100 font-semibold">Conversa</span>
          <span class="kanban-row__value text-slate-900 dark:text-white">#{{ conversation.display_id || conversation.id }}</span>
        </div>
        <div class="kanban-row">
          <span class="kanban-row__label text-slate-700 dark:text-slate-100 font-semibold">Funil</span>
          <span class="kanban-row__value text-slate-900 dark:text-white">{{ kanbanFunnel.name }}</span>
        </div>
        <div class="kanban-row">
          <span class="kanban-row__label text-slate-700 dark:text-slate-100 font-semibold">Etapa atual</span>
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold text-white"
            :style="{ backgroundColor: kanbanStage.color || '#3B82F6' }"
          >
            {{ kanbanStage.name }}
          </span>
        </div>
        <div class="kanban-row">
          <span class="kanban-row__label text-slate-700 dark:text-slate-100 font-semibold">Prioridade</span>
          <span class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium" :class="priorityStyle">
            {{ priorityLabel }}
          </span>
        </div>
        <button
          class="w-full rounded-lg px-3 py-2 text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          @click="goToKanban"
        >
          Abrir board completo
        </button>
      </div>
    </div>

    <div v-else class="space-y-3">
      <p class="text-sm text-slate-500 dark:text-slate-400 text-center">
        Esta conversa nao esta em um pipeline
      </p>

      <div v-if="isLoadingFunnels" class="text-xs text-slate-500 dark:text-slate-400 text-center py-2">
        Carregando funis...
      </div>

      <div v-else-if="hasAvailableFunnels" class="space-y-2.5">
        <div class="space-y-1">
          <label class="text-xs font-medium text-slate-600 dark:text-slate-300">
            Selecione um Funil
          </label>
          <select
            v-model.number="selectedFunnelId"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900"
            :disabled="isLinking"
          >
            <option :value="null">-- Selecione --</option>
            <option
              v-for="funnel in availableFunnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
        </div>

        <div v-if="selectedLinkFunnelId" class="space-y-1">
          <label class="text-xs font-medium text-slate-600 dark:text-slate-300">
            Selecione uma Etapa
          </label>
          <select
            v-model.number="selectedStageIdForLink"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900"
            :disabled="isLoadingLinkStages || isLinking"
          >
            <option :value="null">
              {{ isLoadingLinkStages ? 'Carregando etapas...' : '-- Selecione --' }}
            </option>
            <option
              v-for="stage in linkFunnelStages"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.name }}
            </option>
          </select>
        </div>

        <button
          class="w-full rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors"
          :class="canLinkToPipeline
            ? 'bg-blue-700 hover:bg-blue-800 shadow-sm ring-1 ring-blue-700/40'
            : 'bg-slate-300 text-slate-700 dark:bg-slate-700 dark:text-slate-200 cursor-not-allowed opacity-90'"
          :disabled="!canLinkToPipeline"
          @click="linkToPipeline"
        >
          {{ isLinking ? 'Vinculando...' : 'Vincular ao Pipeline' }}
        </button>
      </div>

      <div v-else class="space-y-2">
        <p class="text-xs text-slate-500 dark:text-slate-400 text-center">
          Nenhum funil disponivel para vinculacao.
        </p>
        <button
          class="w-full px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          @click="goToKanban"
        >
          Configurar Pipeline
        </button>
      </div>
    </div>

    <woot-modal
      :show="Boolean(expandedNote)"
      :on-close="closeExpandedNote"
      size="kanban-note-modal-container"
    >
      <div class="kanban-note-modal-wrapper">
        <div class="kanban-note-modal-header">
          <h3 class="kanban-note-modal-title">Nota completa</h3>
        </div>

        <div class="kanban-note-modal-scroll">
          <p class="kanban-note-modal-meta">
            {{ expandedNote?.created_by_name || 'Agente' }} - {{ formatTimestamp(expandedNote?.created_at) }}
          </p>

          <div class="kanban-note-modal-body">
            <pre class="kanban-note-modal-content">{{ expandedNote?.content || '' }}</pre>
          </div>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped>
.kanban-widget {
  padding: 0;
  --kw-text-muted: rgb(var(--slate-11, 100 116 139));
  --kw-text-strong: rgb(var(--slate-12, 15 23 42));
  --kw-surface-strong: rgb(var(--surface-2, 255 255 255));
  --kw-surface-soft: rgb(var(--surface-1, 248 250 252));
  --kw-border: rgba(var(--border-strong, 226 227 231), 1);
  --kw-border-subtle: rgba(var(--border-weak, 234 234 234), 1);
  --kw-timeline-label: rgb(var(--slate-11, 51 65 85));
}

:global(html.dark) .kanban-widget,
:global(body.dark) .kanban-widget,
:global(.dark) .kanban-widget,
:global([data-theme='dark']) .kanban-widget {
  --kw-text-muted: rgb(var(--slate-11, 203 213 225));
  --kw-text-strong: rgb(var(--slate-12, 248 250 252));
  --kw-surface-strong: rgba(15, 23, 42, 0.95);
  --kw-surface-soft: rgba(15, 23, 42, 0.88);
  --kw-border: rgba(71, 85, 105, 0.9);
  --kw-border-subtle: rgba(71, 85, 105, 0.85);
  --kw-timeline-label: rgb(var(--slate-11, 203 213 225));
}

.kanban-tab,
.kanban-detail-tab {
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--kw-text-muted);
  background: transparent;
  transition: all 0.2s ease;
}

.kanban-tab.is-active,
.kanban-detail-tab.is-active {
  color: var(--kw-text-strong);
  background: var(--kw-surface-strong);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

:global(html.dark) .kanban-tab,
:global(html.dark) .kanban-detail-tab,
:global(body.dark) .kanban-tab,
:global(body.dark) .kanban-detail-tab,
:global(.dark) .kanban-tab,
:global(.dark) .kanban-detail-tab,
:global([data-theme='dark']) .kanban-tab,
:global([data-theme='dark']) .kanban-detail-tab {
  color: #94a3b8;
}

:global(html.dark) .kanban-tab.is-active,
:global(html.dark) .kanban-detail-tab.is-active,
:global(body.dark) .kanban-tab.is-active,
:global(body.dark) .kanban-detail-tab.is-active,
:global(.dark) .kanban-tab.is-active,
:global(.dark) .kanban-detail-tab.is-active,
:global([data-theme='dark']) .kanban-tab.is-active,
:global([data-theme='dark']) .kanban-detail-tab.is-active {
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.95);
  box-shadow: 0 1px 2px rgba(2, 6, 23, 0.4);
}

.kanban-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.kanban-row__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--kw-text-muted) !important;
}

.kanban-row__value {
  font-size: 13px;
  font-weight: 600;
  color: var(--kw-text-strong) !important;
}

:global(html.dark) .kanban-row__label,
:global(body.dark) .kanban-row__label,
:global(.dark) .kanban-row__label,
:global([data-theme='dark']) .kanban-row__label {
  color: rgb(var(--slate-11, 203 213 225)) !important;
}

:global(html.dark) .kanban-row__value,
:global(body.dark) .kanban-row__value,
:global(.dark) .kanban-row__value,
:global([data-theme='dark']) .kanban-row__value {
  color: rgb(var(--slate-12, 248 250 252)) !important;
}

.kanban-stat-card {
  border: 1px solid var(--kw-border);
  border-radius: 8px;
  padding: 8px;
  background: var(--kw-surface-soft);
}

.kanban-stat-card__label {
  font-size: 11px;
  font-weight: 600;
  color: var(--kw-text-muted);
  margin-bottom: 2px;
}

.kanban-stat-card__value {
  font-size: 13px;
  font-weight: 700;
  color: var(--kw-text-strong);
}

.kanban-timeline {
  display: flex;
  align-items: stretch;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.kanban-timeline::-webkit-scrollbar {
  height: 4px;
}

.kanban-timeline::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.35);
  border-radius: 999px;
}

:global(html.dark) .kanban-timeline::-webkit-scrollbar-thumb,
:global(body.dark) .kanban-timeline::-webkit-scrollbar-thumb,
:global(.dark) .kanban-timeline::-webkit-scrollbar-thumb,
:global([data-theme='dark']) .kanban-timeline::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.5);
}

.timeline-stage {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--kw-border);
  border-radius: 999px;
  background: var(--kw-surface-strong);
  padding: 4px 9px;
  min-width: max-content;
  transition: all 0.2s ease;
}

.timeline-stage.is-current {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.14);
}

.timeline-stage.is-past {
  border-color: var(--kw-border-subtle);
  background: var(--kw-surface-soft);
}

.timeline-stage:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.timeline-stage__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}

.timeline-stage__label {
  font-size: 11px;
  font-weight: 600;
  color: var(--kw-timeline-label);
  white-space: nowrap;
}

.kanban-note-modal-wrapper {
  background: var(--kw-surface-strong);
  border: 1px solid var(--kw-border);
  border-radius: 12px;
  width: 100%;
  max-width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

:global(.dark) .kanban-note-modal-wrapper {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.kanban-note-modal-header {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--kw-border);
  overflow-x: hidden;
}

.kanban-note-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--kw-text-strong);
  margin: 0;
  padding-right: 3rem;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
}

.kanban-note-modal-meta {
  font-size: 13px;
  color: var(--kw-text-muted);
  margin: 0 0 12px;
}

.kanban-note-modal-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 16px 20px 20px;
}

.kanban-note-modal-body {
  background: var(--kw-surface-soft);
  border: 1px solid var(--kw-border);
  border-radius: 8px;
  padding: 16px 18px;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.kanban-note-modal-content {
  display: block;
  width: 100%;
  max-width: 100%;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.7;
  color: var(--kw-text-strong);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  overflow-x: hidden;
  box-sizing: border-box;
  margin: 0;
}
</style>

<style>
.modal-mask .modal-container.kanban-note-modal-container {
  width: min(96vw, 64rem) !important;
  max-width: 96vw !important;
  overflow: hidden !important;
}

@media (min-width: 1536px) {
  .modal-mask .modal-container.kanban-note-modal-container {
    width: min(94vw, 72rem) !important;
  }
}
</style>
